'use client';

import type { FieldError, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';
import formStyle from '../../../styles/modules/form.module.scss';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormText.module.scss';

type Props<T extends FieldValues> = {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  label?: string;
  placeholder?: string;
  autocomplete?: string;
  required?: boolean;
  readOnly?: boolean;
  prefix?: React.ReactNode;
  outlined?: boolean;
  onChange?: () => void;
  isSmall?: boolean;
  hint?: string;
  min?: number;
  max?: number;
} & UseControllerProps<T>;

function FormText<T extends FieldValues>({
  name,
  type = 'text',
  label,
  placeholder,
  autocomplete = 'off',
  required = false,
  readOnly = false,
  control,
  prefix,
  outlined = false,
  onChange,
  isSmall = false,
  hint,
  min,
  max,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const value = e.target.value === '' ? '' : Number(e.target.value);
      if (min !== undefined && value !== '' && value < min) {
        e.target.value = min.toString();
      }
      if (max !== undefined && value !== '' && value > max) {
        e.target.value = max.toString();
      }
    }
    field.onChange(e);
  };

  // クラス名を決定
  const inputClassName = styles.input;

  return (
    <div className={`${formStyle.formComponentStyle}`}>
      {/* ラベル */}
      {label && <FormLabel>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div
        className={`${formStyle.fieldWrapper} ${outlined ? styles.outlined : ''}  ${isSmall ? styles.smallRect : ''}`}
      >
        {prefix}
        <input
          type={type}
          placeholder={placeholder}
          autoComplete={autocomplete}
          required={required}
          name={field.name}
          ref={field.ref}
          onBlur={() => {
            field.onBlur();
            if (onChange) onChange();
          }}
          onChange={handleChange}
          value={field.value}
          className={inputClassName}
          readOnly={readOnly}
          min={min}
          max={max}
          data-testid={`${field.name}-input`}
        />
      </div>
      {hint && <p className={formStyle.hint}>{hint}</p>}
      {/* エラー */}
      {error && (
        <div className={formStyle.validateErrors}>
          <p>{(error as FieldError).message}</p>
        </div>
      )}
    </div>
  );
}
export default FormText;
